<template>
  <div class="screen-editor-container">
    <!-- SVG overlay to draw the quadrilateral -->
    <svg class="screen-svg" :style="svgStyle">
      <polygon :points="svgPoints" fill="rgba(0,255,0,0.12)" stroke="#00ff00" stroke-width="2" />
    </svg>

    <!-- Corner handles -->
    <div
      v-for="(corner, key) in corners"
      :key="key"
      class="handle"
      :style="handleStyle(corner)"
      @mousedown.stop.prevent="startCornerDrag(key, $event)"
    >
      <span class="handle-label">{{ key }}</span>
    </div>

    <!-- Coordinates display -->
    <div class="coords-label" :style="labelStyle">
      TL:({{ r(corners.tl.x) }},{{ r(corners.tl.y) }})
      TR:({{ r(corners.tr.x) }},{{ r(corners.tr.y) }})
      BL:({{ r(corners.bl.x) }},{{ r(corners.bl.y) }})
      BR:({{ r(corners.br.x) }},{{ r(corners.br.y) }})
    </div>
  </div>
</template>

<script setup>
import { reactive, computed, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  canvasEl: Object,
  initialScreen: Object,
  initialCorners: Object
})

const emit = defineEmits(['update'])

const corners = reactive({
  tl: { x: 0, y: 0 },
  tr: { x: 200, y: 0 },
  bl: { x: 0, y: 150 },
  br: { x: 200, y: 150 }
})

let activeCorner = null
let startMouseX = 0
let startMouseY = 0
let startCornerPos = { x: 0, y: 0 }

const r = (v) => Math.round(v)

function getScale() {
  const canvas = props.canvasEl
  if (!canvas) return 1
  return canvas.clientWidth / canvas.width
}

function handleStyle(corner) {
  const s = getScale()
  return {
    left: (corner.x * s - 8) + 'px',
    top: (corner.y * s - 8) + 'px'
  }
}

const svgStyle = computed(() => {
  const canvas = props.canvasEl
  if (!canvas) return {}
  return {
    width: canvas.clientWidth + 'px',
    height: canvas.clientHeight + 'px'
  }
})

const svgPoints = computed(() => {
  const s = getScale()
  return [
    `${corners.tl.x * s},${corners.tl.y * s}`,
    `${corners.tr.x * s},${corners.tr.y * s}`,
    `${corners.br.x * s},${corners.br.y * s}`,
    `${corners.bl.x * s},${corners.bl.y * s}`
  ].join(' ')
})

const labelStyle = computed(() => {
  const s = getScale()
  const minY = Math.min(corners.tl.y, corners.tr.y) * s
  const minX = corners.tl.x * s
  return {
    left: minX + 'px',
    top: (minY - 24) + 'px'
  }
})

function logCoords() {
  const data = {
    tl: { x: r(corners.tl.x), y: r(corners.tl.y) },
    tr: { x: r(corners.tr.x), y: r(corners.tr.y) },
    bl: { x: r(corners.bl.x), y: r(corners.bl.y) },
    br: { x: r(corners.br.x), y: r(corners.br.y) }
  }
  console.log('📐 Corner coordinates:', JSON.stringify(data))
  console.log(`corners: { tl: [${data.tl.x}, ${data.tl.y}], tr: [${data.tr.x}, ${data.tr.y}], bl: [${data.bl.x}, ${data.bl.y}], br: [${data.br.x}, ${data.br.y}] }`)

  // Also emit bounding rect for backward compat
  const minX = Math.min(data.tl.x, data.bl.x)
  const minY = Math.min(data.tl.y, data.tr.y)
  const maxX = Math.max(data.tr.x, data.br.x)
  const maxY = Math.max(data.bl.y, data.br.y)
  console.log(`screen: { x: ${minX}, y: ${minY}, width: ${maxX - minX}, height: ${maxY - minY} }`)

  emit('update', { corners: data, screen: { x: minX, y: minY, width: maxX - minX, height: maxY - minY } })
}

function startCornerDrag(key, e) {
  activeCorner = key
  startMouseX = e.clientX
  startMouseY = e.clientY
  startCornerPos = { x: corners[key].x, y: corners[key].y }
}

function onMouseMove(e) {
  if (!activeCorner) return
  const s = getScale()
  const dx = (e.clientX - startMouseX) / s
  const dy = (e.clientY - startMouseY) / s
  corners[activeCorner].x = startCornerPos.x + dx
  corners[activeCorner].y = startCornerPos.y + dy
}

function onMouseUp() {
  if (activeCorner) {
    logCoords()
    activeCorner = null
  }
}

function initFromScreen(screen) {
  if (!screen) return
  corners.tl.x = screen.x
  corners.tl.y = screen.y
  corners.tr.x = screen.x + screen.width
  corners.tr.y = screen.y
  corners.bl.x = screen.x
  corners.bl.y = screen.y + screen.height
  corners.br.x = screen.x + screen.width
  corners.br.y = screen.y + screen.height
}

function initFromCorners(c) {
  if (!c) return false
  corners.tl.x = c.tl.x; corners.tl.y = c.tl.y
  corners.tr.x = c.tr.x; corners.tr.y = c.tr.y
  corners.bl.x = c.bl.x; corners.bl.y = c.bl.y
  corners.br.x = c.br.x; corners.br.y = c.br.y
  return true
}

onMounted(() => {
  if (!initFromCorners(props.initialCorners)) {
    initFromScreen(props.initialScreen)
  }
  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', onMouseUp)
})

onUnmounted(() => {
  window.removeEventListener('mousemove', onMouseMove)
  window.removeEventListener('mouseup', onMouseUp)
})

watch(() => props.initialScreen, (val) => {
  initFromScreen(val)
}, { deep: true })
</script>

<style scoped>
.screen-editor-container {
  position: absolute;
  inset: 0;
  z-index: 30;
  pointer-events: none;
}

.screen-svg {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
}

.handle {
  position: absolute;
  width: 16px;
  height: 16px;
  background: #00ff00;
  border: 2px solid #000;
  border-radius: 50%;
  cursor: grab;
  pointer-events: all;
  z-index: 31;
}

.handle:active {
  cursor: grabbing;
  background: #00cc00;
}

.handle-label {
  position: absolute;
  top: -18px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 9px;
  color: #00ff00;
  font-family: monospace;
  text-transform: uppercase;
  white-space: nowrap;
}

.coords-label {
  position: absolute;
  font-size: 10px;
  color: #00ff00;
  background: rgba(0, 0, 0, 0.85);
  padding: 3px 8px;
  border-radius: 3px;
  white-space: nowrap;
  font-family: monospace;
  pointer-events: none;
}
</style>
